import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PAGE_META } from '../../lib/seoMeta';
import { ArrowRight, Scale } from 'lucide-react';
import Breadcrumbs from '../Breadcrumbs';
import InternalLinks from '../InternalLinks';
import { COMPARISON_LIST } from '../../data/comparisons';

const CAT_LABEL = { badkamer: 'Badkamer', keuken: 'Keuken', technieken: 'Technieken', totaal: 'Totaalrenovatie', interieur: 'Interieur' };

const VergelijkIndex = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const listSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Renovatie-vergelijkingen van Max Q',
    itemListElement: COMPARISON_LIST.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.h1, url: `https://maxq.be/vergelijk/${c.slug}` })),
  };

  return (
    <div data-testid="vergelijk-index">
      <Helmet>
        <title>{PAGE_META.vergelijk.title}</title>
        <meta name="description" content={PAGE_META.vergelijk.description} />
        <link rel="canonical" href="https://maxq.be/vergelijk" />
        <script type="application/ld+json">{JSON.stringify(listSchema)}</script>
      </Helmet>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Vergelijkingen' }]} />

      <section className="bg-[#f4efe9] py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4">
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#3a190b]/70 mb-5"><Scale className="h-4 w-4" aria-hidden="true" />Beslissingshulp</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#202020] leading-[1.05] mb-6">Dit of dat? Wij vergelijken eerlijk.</h1>
          <p className="text-base md:text-lg text-[#202020]/80 max-w-3xl">Elke renovatie begint met keuzes. Hieronder zetten we de populairste twijfels naast elkaar: met prijzen, voor- en nadelen en het advies dat we ook aan onze eigen klanten geven.</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {COMPARISON_LIST.map((c, i) => (
            <Link key={c.slug} to={`/vergelijk/${c.slug}`} className="group relative rounded-2xl border border-[#3a190b]/10 bg-white p-7 hover:border-[#3a190b]/40 hover:shadow-lg transition-all" data-testid={`vergelijk-card-${c.slug}`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className="text-xs uppercase tracking-wider text-[#3a190b]/60">{CAT_LABEL[c.category] || 'Renovatie'}</span>
              <div className="mt-4 mb-5 flex items-center gap-3 text-sm font-semibold">
                <span className="px-3 py-1.5 rounded-full bg-[#3a190b] text-white">{c.a.name}</span>
                <span className="text-[#3a190b]/50 font-black text-xs">VS</span>
                <span className="px-3 py-1.5 rounded-full bg-[#f4efe9] text-[#3a190b]">{c.b.name}</span>
              </div>
              <h2 className="text-lg md:text-xl font-bold text-[#202020] mb-2 group-hover:text-[#3a190b] transition-colors">{c.title}</h2>
              <p className="text-sm text-[#202020]/65 line-clamp-3">{c.summary}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#3a190b]">Lees de vergelijking<ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
            </Link>
          ))}
        </div>
      </section>

      <InternalLinks />
    </div>
  );
};

export default VergelijkIndex;
